import { useCreateInvoice } from '@repo/api-codegen';
import { useCallback, useState } from 'react';
import { CREDIT_PURCHASE_DEFAULTS, CREDIT_PURCHASE_LIMITS } from './constants';

interface UseBuyCreditsModalProps {
	onPurchase: (amount: number) => void;
}

export function useBuyCreditsModal({ onPurchase }: UseBuyCreditsModalProps) {
	const [amount, setAmount] = useState<number>(CREDIT_PURCHASE_DEFAULTS.AMOUNT);
	const [amountError, setAmountError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { mutate: createInvoice, isPending: isCreatingInvoice } =
		useCreateInvoice();

	const validateAmount = useCallback((value: number | ''): string | null => {
		if (value === '' || value === null || value === undefined) {
			return 'Amount is required';
		}
		if (value < CREDIT_PURCHASE_LIMITS.MIN) {
			return `Amount must be at least $${CREDIT_PURCHASE_LIMITS.MIN}`;
		}
		if (value > CREDIT_PURCHASE_LIMITS.MAX) {
			return `Amount cannot exceed $${CREDIT_PURCHASE_LIMITS.MAX}`;
		}
		return null;
	}, []);

	const handlePurchase = useCallback(() => {
		const validationError = validateAmount(amount);
		setAmountError(validationError);

		if (validationError) {
			return;
		}

		createInvoice(
			{
				body: {
					amount,
					description: `Credits Purchase`,
				},
			},
			{
				onSuccess: () => {
					onPurchase(amount);
				},
				onError: (error) => {
					setError(error?.detail || 'An error occurred');
				},
			}
		);
	}, [amount, createInvoice, onPurchase, validateAmount]);

	const handleAmountChange = useCallback(
		(value: number | '') => {
			const numValue =
				typeof value === 'number' ? value : CREDIT_PURCHASE_DEFAULTS.AMOUNT;
			setAmount(numValue);
			const error = validateAmount(value);
			setAmountError(error);
		},
		[validateAmount]
	);

	const handleReset = useCallback(() => {
		const newAmount = CREDIT_PURCHASE_DEFAULTS.AMOUNT;
		setAmount(newAmount);
		setAmountError(null);
	}, []);

	return {
		amount,
		amountError,
		error,
		isCreatingInvoice,
		handlePurchase,
		handleAmountChange,
		handleReset,
	};
}
